import React, { FC, useState } from 'react';
import cls from 'classnames';
import { DatePickerCalendarPartialSheetProps } from './DatePickerCalendarPartialSheet.types';
import { getClassNames, getRootClassListFromProps } from '../../../utilities/classNames';
import {
	defaultMaxDate,
	defaultMinDate,
} from '../../../components/control/datePickerCalendar/DatePickerCalendar';
import DatePickerCalendar from '../../../components/control/datePickerCalendar';
import { PartialSheet } from '../../../components/sheet/partialSheet';
export const classNames = getClassNames('sds-date-picker-calendar-partial-sheet', []);

/**
 * ## Usage
 * `DatePickerCalendarPartialSheet` integrates a datepicker into a partial sheet which
 * is invoked by a button.
 * The button will typically render the value of the selected date.
 * For maximum flexibility the rendering of the button is the responsibility of the consumer via a children render prop.
 *
 * example:
 *
 * ```jsx
 * <DatePickerCalendarPartialSheet
 *			value={value}
 *			onDayClicked={(newValue) => setValue(newValue)}
 *		>
 *			{(value, handleDateValueClick) => (
 *				<button onClick={handleDateValueClick}>{value.toDateString()}</button>
 *			)}
 *		</DatePickerCalendarPartialSheet>
 *
 * ```
 *
 * @param props
 * @returns
 */
const DatePickerCalendarPartialSheet: FC<DatePickerCalendarPartialSheetProps> = (
	props: DatePickerCalendarPartialSheetProps
) => {
	const {
		visibleDate: initialVisibleDate,
		value,
		className,
		children,
		size,
		variant,
		minDate = defaultMinDate,
		maxDate = defaultMaxDate,
		onDayClicked,
	} = props;
	const [visibleDate, setVisibleDate] = useState(initialVisibleDate);
	const [showPartialSheet, setShowPartialSheet] = useState(false);
	const handleDateValueClick = () => {
		setVisibleDate(initialVisibleDate);
		setShowPartialSheet(true);
	};
	return (
		<div
			className={cls(getRootClassListFromProps(classNames, props), className)}
			data-testid={props['data-testid']}
		>
			{typeof children === 'function' && children(value, handleDateValueClick)}
			{showPartialSheet && (
				<PartialSheet onClose={() => setShowPartialSheet(false)}>
					<DatePickerCalendar
						aria-label="Date picker"
						size={size}
						variant={variant}
						minDate={minDate}
						maxDate={maxDate}
						selectedDate={value}
						visibleDate={visibleDate}
						onNextMonthButtonClicked={(date) => {
							setVisibleDate(date);
						}}
						onPreviousMonthButtonClicked={(date) => {
							setVisibleDate(date);
						}}
						onFilterClicked={(date) => {
							setVisibleDate(date);
						}}
						onDayClicked={(d) => {
							onDayClicked(d);
							setShowPartialSheet(false);
						}}
					/>
				</PartialSheet>
			)}
		</div>
	);
};
DatePickerCalendarPartialSheet.displayName = 'DatePickerCalendarPartialSheet';

export default DatePickerCalendarPartialSheet;
